import {
  Box,
  Card,
  Flex,
  useDisclosure,
  HStack,
  Spinner,
  Text,
  SimpleGrid,
  IconButton,
  Grid,
  GridItem,
  useToken,
  VStack,
  Heading,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";

import {
  CommandBar,
  CommandBarContent,
  CommandBarInput,
  CommandBarList,
  CommandBarItem,
  CommandBarLoading,
  CommandBarEmpty,
} from "@saas-ui/command-bar";
import {
  EmptyState,
  Banner,
  BannerTitle,
  BannerDescription
} from "@saas-ui/react";
import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { FiChevronDown, FiVideo, FiX, FiYoutube } from "react-icons/fi";
import { useNavigate, useParams } from "react-router";
import {
  useCompareAudienceMutation,
  useCompareCompositeSentimentMutation,
  useCompareEmotionMutation,
  useCompareInterestsMutation,
  useGetMediaQuery,
  useLazyOpenEndedQuery,
  useLazyTopicSizingQuery,
  useOpenEndedQuery,
  useTopicSizingQuery,
} from "../../../store/services/mediaApi";
import { MdMovie } from "react-icons/md";
import { MediaTypeTag } from "../../util/Tags";
import useFormSnackbar from "../../util/FormSnackbar";
import TabManager from "../../util/TabManager";
import { AudienceBase } from "../views/Audience";
import { EmotionChart } from "../views/Emotion";
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";
import { useNormSelector } from "../NormContext";
import { MediaOverallMetricBase } from "../MediaItemCards";
import CompareSurveys from "./CompareSurvey";
import FeatureFlag from "../../util/FeatureFlag";
import { useOpenEndedSource } from "../OpenEndedContext";
import { createColumnHelper } from "@tanstack/react-table";
import CompareTable from "../../../Table/CompareTable";

const CompareContext = createContext();
export const useCompareContext = () => useContext(CompareContext);

export const CompareProvider = ({ children }) => {
  const [selectedValues, setSelectedValues] = useState([]);
  const { selectedNorm } = useNormSelector();

  // Load selectedValues from local storage on initial render
  useEffect(() => {
    const savedValues =
      JSON.parse(localStorage.getItem("selectedValues")) || [];
    setSelectedValues(savedValues);
  }, []);

  // Update local storage whenever selectedValues change
  useEffect(() => {
    localStorage.setItem("selectedValues", JSON.stringify(selectedValues));
  }, [selectedValues]);

  const getFormattedBody = () => {
    const values = {
      media_items: selectedValues?.map((ithMI) => ithMI?.media_item_id),
      media_item_meta: selectedValues,
      norm: "current",
      grouping_strategy: "grouped",
    };
    return values;
  };

  const value = {
    selectedValues,
    setSelectedValues,
    getFormattedBody,
  };

  return (
    <CompareContext.Provider value={value}>{children}</CompareContext.Provider>
  );
};

const Compare = () => {
  const { client } = useParams();
  const { data, isLoading } = useGetMediaQuery(client);

  const { selectedValues, setSelectedValues } = useCompareContext();

  const [searchValue, setSearchValue] = useState("");
  const { isOpen, onClose, onToggle, onOpen } = useDisclosure();

  const onSelect = (v) => {
    setSelectedValues([v, ...selectedValues]);
    onToggle();
    setSearchValue("");
  };

  const setSearch = (v) => {
    if (v === "") {
      onClose();
    } else {
      onOpen();
    }
    setSearchValue(v);
  };
  const navigate = useNavigate();

  const tabs = [
    {
      value: `overview`,
      title: "Overview",
      content: <OverviewCompare />,
    },
    {
      value: `survey`,
      title: "Surveys",
      content: <CompareSurveys />,
    },
    {
      value: `audience`,
      title: "Audience",
      content: <CompareAudience />,
    },
    {
      value: `sentiment`,
      title: "Sentiment",
      content: <CompareSentiment />,
    },
    {
      value: `open-ended`,
      title: "Open Ended",
      content: <CompareOpenEnded />,
    },
  ];

  const filteredData = data?.filter(
    (item) =>
      !selectedValues.some(
        (selected) => selected.media_item_id === item.media_item_id
      )
  );

  return (
    <Flex flexDirection="row" minH="100vh">
      <Flex flex="3" direction="column">
        <Box>
          <CommandBar bg="WhiteAlpha.200">
            <CommandBarContent>
              <CommandBarInput
                size="sm"
                value={searchValue}
                onChange={(v) => setSearch(v)}
                placeholder="Type in the name of title..."
                autoFocus
              />

              {isOpen && (
                <CommandBarList>
                  {isLoading && <CommandBarLoading>Hang on…</CommandBarLoading>}

                  <CommandBarEmpty>No results found.</CommandBarEmpty>

                  {filteredData?.map((v) => {
                    return (
                      <CommandBarItem
                        key={v.media_item_id}
                        value={v.media_item_id}
                        onSelect={() => onSelect(v)}
                      >
                        <HStack py="1">
                          <MdMovie />
                          <VStack align="left" gap="0">
                            <Text fontSize={"sm"} fontWeight={"medium"}>
                              {v.title}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {v?.subtitle}
                            </Text>
                          </VStack>
                        </HStack>
                      </CommandBarItem>
                    );
                  })}
                </CommandBarList>
              )}
            </CommandBarContent>
          </CommandBar>
          <HStack mt="2" justifyContent="space-between">
            <HStack>
              {selectedValues?.map((ithMediaItem) => (
                <Box
                  bg="white"
                  p="2"
                  fontSize="sm"
                  borderRadius="md"
                  boxShadow="sm"
                  fontWeight="medium"
                  key={`item-${ithMediaItem?.media_item_id}`}
                  onClick={() =>
                    navigate(
                      `/${client}/media/${ithMediaItem?.media_item_id}/overview`
                    )
                  }
                  _hover={{ cursor: "pointer" }}
                >
                  <HStack>
                    <MediaTypeTag
                      mediaType={ithMediaItem?.media_type_id}
                      size="small"
                    />
                    <Text>{ithMediaItem?.subtitle}</Text>
                  </HStack>
                </Box>
              ))}
            </HStack>
            <HStack>
              {!!selectedValues?.length && (
                <IconButton
                  variant="ghost"
                  onClick={() => {
                    setSelectedValues([]);
                    setSearchValue("");
                  }}
                >
                  <FiX />
                </IconButton>
              )}
            </HStack>
          </HStack>
        </Box>

        <TabManager
          tabs={tabs}
          defaultTab={tabs[0]?.value}
          basePath={`/${client}/compare/`}
        />
      </Flex>
    </Flex>
  );
};

const CompareOpenEnded = () => {
  const [selectedSource, setSelectedSource] = useState('VolKno')
  let { selectedValues } = useCompareContext();
  const [loadingStatus, setLoadingStatus] = useState('not loaded')

  const [isComparePossible, setIsComparePossible] = useState(true)
  const [fetchOpenEnded] = useLazyOpenEndedQuery();
  const [openEndedList, setOpenEndedList] = useState([]);

  const last = selectedValues.at(-1);
  const mediaIdToFetch = last?.media_item_id;
  const { data: topicSizing } = useOpenEndedQuery(mediaIdToFetch, {
    skip: !mediaIdToFetch,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!selectedValues || selectedValues.length === 0) return;

    setLoadingStatus('loading');
    setIsComparePossible(true); // assume true, verify later

    const fetchAll = async () => {
      let hasFailureOrEmpty = false;
      const results = await Promise.all(
        selectedValues.map(async (item) => {
          try {
            const res = await fetchOpenEnded(item.media_item_id).unwrap();
            if (Object.keys(res).length === 0) {
              hasFailureOrEmpty = true;
              return null;
            }
            return res;
          } catch (err) {
            console.error(`Failed to fetch topic sizing for ${item.media_item_id}`, err);
            hasFailureOrEmpty = true;
            return null;
          }
        })
      );

      if (hasFailureOrEmpty) {
        setIsComparePossible(false);
      }

      const validResults = results
        .map((res, idx) => {
          if (!res) return null;
          const mediaId = selectedValues[idx].media_item_id;
          return { [mediaId]: res };
        })
        .filter(Boolean);

      const mergedResult = Object.assign({}, ...validResults);

      setOpenEndedList(mergedResult);
      setLoadingStatus('loaded');
    };

    fetchAll();
  }, [selectedValues.map((v) => v.media_item_id).join(',')]);

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("comment", {
      header: "Comment",
    }),
    columnHelper.accessor("sentiment", {
      header: "Sentiment",
    }),

  ];

  const getIcon = (source) => {
    switch (source) {
      case "VolKno":
        return <FiVideo />;
      case "topic_sizing":
      default:
        return <FiYoutube />;
    }
  };

  const options = Object.keys(topicSizing || {}) || [];

  function findCommonTopics(data, tab) {
    const mediaIds = Object.keys(data);
    if (mediaIds.length < 2) return [];

    const topicToMediaSet = new Map(); 
    const groupedComments = new Map();

    for (const mediaId of mediaIds) {
      const comments = data[mediaId]?.[tab] || [];

      for (const { sentiment, comment: commentText, topics } of comments) {
        if (!Array.isArray(topics)) continue;

        for (const { topic } of topics) {
          // Build topic → media set
          if (!topicToMediaSet.has(topic)) {
            topicToMediaSet.set(topic, new Set());
          }
          topicToMediaSet.get(topic).add(mediaId);

          // Pre-group all comments
          if (!groupedComments.has(topic)) {
            groupedComments.set(topic, []);
          }

          groupedComments.get(topic).push({
            id: Number(mediaId),
            sentiment,
            comment: commentText,
          });
        }
      }
    }

    const commonTopics = [];
    for (const [topic, mediaSet] of topicToMediaSet.entries()) {
      if (mediaSet.size === mediaIds.length) {
        commonTopics.push(topic);
      }
    }

    return commonTopics.map((topic) => ({
      topic,
      media: groupedComments.get(topic) || [],
    }));
  }

  const data = useMemo(() => {
    return findCommonTopics(openEndedList, selectedSource);
  }, [openEndedList, selectedSource]);

  const noSelection = !selectedValues || selectedValues.length <= 1;
  const noMatchingOpenEnded =
    (loadingStatus === 'loaded' && (!isComparePossible || data.length === 0));


  if (noSelection) {
    return (
      <EmptyState
        title="Awaiting Selection"
        description="Please select a set of titles to begin comparing open-ended data. Once selected, you'll be able to view and compare open-ended results here."
      />
    );
  }

  const downloadCsv = (data, filename) => {
    let csvContent = 'Media,Comment,Description,Sentiment\n';

    data.forEach((topicBlock) => {
      const topic = topicBlock.topic;

      topicBlock.media.forEach((media) => {
        const selectedItem = selectedValues.find(
          (item) => item.media_item_id === media?.id
        );

        const row = [
          selectedItem?.subtitle ?? media?.id,
          topic,
          `"${media.comment.replace(/"/g, '""')}"`,
          media.sentiment,
        ];

        csvContent += row.join(',') + '\n';
      });
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <HStack w="100%" overflowX="auto" justifyContent="space-between" mb="3">
        <HStack spacing={2}>
          {options.map((option) => {
            const icon = getIcon(option);
            const isSelected = option === selectedSource
            return (
              <Button
                key={option}
                size="sm"
                colorScheme={isSelected ? "red" : "gray"}
                onClick={() => setSelectedSource(option)}
                leftIcon={icon}
              >
                {option}
              </Button>
            );
          })}
        </HStack>
        {data.length > 0 && (
          <Menu>
            <MenuButton
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              minWidth="100px"
              px={4}
              as={Button}
              variant="outline"
              rightIcon={<FiChevronDown />}
            >
              Export
            </MenuButton>
            <MenuList minWidth="100px">
              <MenuItem
                onClick={() => {
                  downloadCsv(data, 'compare-open-ended-data.csv');
                }}
              >
                <HStack align="start">
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold">
                      CSV
                    </Text>
                  </Box>
                </HStack>
              </MenuItem>
            </MenuList>
          </Menu>
        )}
      </HStack>
      {noMatchingOpenEnded ?
        <EmptyState
          title="No Matching Open-Ended Data Found"
          description="Your selection unfortunately did not match any available open-ended feedback. This could be due to a lack of common data among the selected titles, or there are no open-ended feedback available for them at this moment. Please adjust your selection criteria or try selecting different titles."
        />
        :
        <SimpleGrid columns={1} spacing={5} width="100%">
          <Card p={4} flex="1">
            <HStack justifyContent="space-between">
              <Heading p="3" size="sm">
                {""}
              </Heading>
              {/* <HStack>
              <FeatureFlag description="The most frequent topics mentioned across all open-ended feedback are listed here, depending on whether they were mentioned positively or negatively" />
            </HStack> */}
            </HStack>
            <Flex width="100%">
              <CompareTable
                data={data}
                columns={columns}
                isLoading={loadingStatus === 'loading'}
                simplify={true}
                selectedValues={selectedValues}
              />
            </Flex>
          </Card>
        </SimpleGrid>
      }
    </>
  )
}

const CompareSentiment = () => {
  const [selectedSource, setSelectedSource] = useState('VolKno')
  const { selectedValues } = useCompareContext();
  const [loadingStatus, setLoadingStatus] = useState('not loaded')

  const [isComparePossible, setIsComparePossible] = useState(true)
  const [topicSizingList, setTopicSizingList] = useState({});
  const [fetchTopicSizing] = useLazyTopicSizingQuery();

  const last = selectedValues.at(-1);
  const mediaIdToFetch = last?.media_item_id;
  const { data: topicSizing } = useTopicSizingQuery(mediaIdToFetch, {
    skip: !mediaIdToFetch,
    refetchOnMountOrArgChange: true,
  });


  useEffect(() => {
    if (!selectedValues || selectedValues.length === 0) return;

    setLoadingStatus('loading');
    setTopicSizingList({});
    setIsComparePossible(true);

    const fetchAll = async () => {
      let hasFailureOrEmpty = false;
      const results = await Promise.all(
        selectedValues.map(async (item) => {
          try {
            const res = await fetchTopicSizing(item.media_item_id).unwrap();

            if (Object.keys(res).length === 0) {
              hasFailureOrEmpty = true;
              return null;
            }


            return res;
          } catch (err) {
            console.error(`Failed to fetch topic sizing for ${item.media_item_id}`, err);
            hasFailureOrEmpty = true;
            return null;
          }
        })
      );

      if (hasFailureOrEmpty) {
        setIsComparePossible(false);
      }

      const validResults = results
        .map((res, idx) => {
          if (!res) return null;
          const mediaId = selectedValues[idx].media_item_id;
          return { [mediaId]: res };
        })
        .filter(Boolean);

      const mergedResult = Object.assign({}, ...validResults);

      setTopicSizingList(mergedResult);
      setLoadingStatus('loaded');
    };

    fetchAll();
  }, [selectedValues.map((v) => v.media_item_id).join(',')]);

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("description", {
      header: "Description",
    }),
    columnHelper.accessor("positive", {
      header: "Positive",
    }),
    columnHelper.accessor("negative", {
      header: "Negative",
    }),
    columnHelper.accessor("total", {
      header: "Sentiment Breakdown",
    }),
  ];

  const getIcon = (source) => {
    switch (source) {
      case "VolKno":
        return <FiVideo />;
      case "topic_sizing":
      default:
        return <FiYoutube />;
    }
  };

  const options = Object.keys(topicSizing || {}) || [];

  function findCommonTopics(data, tab) {
    const topicMap = {};

    for (const mediaId in data) {
      const entries = data[mediaId][tab] || [];
      for (const entry of entries) {
        const topic = entry.topic;
        if (!topicMap[topic]) {
          topicMap[topic] = [];
        }
        topicMap[topic].push({
          id: Number(mediaId),
          description: entry.description,
          negative: entry.negative_percentage,
          positive: entry.positive_percentage,
          total: entry.total,
        });
      }
    }

    // Filter only topics that appear in multiple media IDs
    const commonTopics = Object.entries(topicMap)
      .filter(([_, media]) => media.length > 1)
      .map(([topic, media]) => ({ topic, media }));

    return commonTopics;
  }

  const data = useMemo(() => {
    return findCommonTopics(topicSizingList, selectedSource);
  }, [topicSizingList, selectedSource]);

  const noSelection = !selectedValues || selectedValues.length <= 1;
  const noMatchingSentiment =
    (loadingStatus === 'loaded' && (!isComparePossible || data.length === 0));

  if (noSelection) {
    return (
      <EmptyState
        title="Awaiting Selection"
        description="Please select a set of titles to begin comparing sentiment data. Once selected, you'll be able to view and compare sentiment results here."
      />
    );
  }

  const downloadCsv = (data, filename) => {
    let csvContent = 'Media,Topic,Description,Positive,Negative,Total\n';

    data.forEach((topicBlock) => {
      const topic = topicBlock.topic;

      topicBlock.media.forEach((media) => {
        const selectedItem = selectedValues.find(
          (item) => item.media_item_id === media?.id
        );

        const row = [
          selectedItem?.subtitle ?? media?.id,
          topic,
          `"${media.description.replace(/"/g, '""')}"`, // escape quotes
          media.positive,
          media.negative,
          media.total,
        ];

        csvContent += row.join(',') + '\n';
      });
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <HStack w="100%" overflowX="auto" justifyContent="space-between" mb="3">
        <HStack spacing={2}>
          {options.map((option) => {
            const icon = getIcon(option);
            const isSelected = option === selectedSource;
            return (
              <Button
                key={option}
                size="sm"
                colorScheme={isSelected ? "red" : "gray"}
                onClick={() => setSelectedSource(option)}
                leftIcon={icon}
              >
                {option}
              </Button>
            );
          })}
        </HStack>
        {data.length > 0 && (
          <Menu>
            <MenuButton
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              minWidth="100px"
              px={4}
              as={Button}
              variant="outline"
              rightIcon={<FiChevronDown />}
            >
              Export
            </MenuButton>
            <MenuList minWidth="100px">
              <MenuItem
                onClick={() => {
                  downloadCsv(data, 'compare-data.csv');
                }}
              >
                <HStack align="start">
                  <Box>
                    <Text fontSize="sm" fontWeight="semibold">
                      CSV
                    </Text>
                  </Box>
                </HStack>
              </MenuItem>
            </MenuList>
          </Menu>
        )}
      </HStack>
      {noMatchingSentiment
        ?
        <EmptyState
          title="No Matching Sentiment Found"
          description="Your selection unfortunately did not match any available sentiment. This could be due to a lack of common sentiment data among the selected titles, or there are no sentiment available for them at this moment. Please adjust your selection criteria or try selecting different titles."
        />
        :
        <SimpleGrid columns={1} spacing={5} width="100%">
          <Card p={4} flex="1">
            <HStack justifyContent="space-between">
              <Heading p="3" size="sm">
                {""}
              </Heading>
              {/* <HStack>
              <FeatureFlag description="The most frequent topics mentioned across all open-ended feedback are listed here, depending on whether they were mentioned positively or negatively" />
            </HStack> */}
            </HStack>
            <Flex width="100%">
              <CompareTable
                data={data}
                columns={columns}
                columnsToFilter={[]}
                isLoading={loadingStatus === 'loading'}
                simplify={true}
                selectedValues={selectedValues}
              />
            </Flex>
          </Card>
        </SimpleGrid>
      }
    </>
  )
}

const CompareComposite = () => {
  const { getFormattedBody, selectedValues } = useCompareContext();
  const [postSentiment, { data: sentimentResponse, isLoading }] =
    useCompareCompositeSentimentMutation();

  useEffect(() => {
    // Ensure there are selected values before calling the API
    if (selectedValues.length > 1) {
      postSentiment(getFormattedBody());
    }
  }, [selectedValues, postSentiment, getFormattedBody]);

  return (
    <>
      {selectedValues?.map((movie, idx) => {
        const selectedData = sentimentResponse?.find(
          (v) => v?.media_item_id === movie?.media_item_id
        );

        return (
          <MediaOverallMetricBase
            key={movie?.media_item_id || idx} // Add a unique key
            sentimentComposite={selectedData}
            isLoading={isLoading}
          />
        );
      })}
    </>
  );
};

const EmotionalCompare = () => {
  const { getFormattedBody, selectedValues } = useCompareContext();
  const [postEmotion, emotionResponse] = useCompareEmotionMutation();

  useFormSnackbar(emotionResponse, "Error Fetching Emotions");

  useEffect(() => {
    // Ensure there are selected values before calling the API
    if (selectedValues.length > 1) {
      postEmotion(getFormattedBody());
    }
  }, [selectedValues, postEmotion]);

  return selectedValues?.map((movie, idx) => (
    <Card>
      <Flex height="100%" justifyContent="center" alignItems="center">
        {emotionResponse?.isLoading ? (
          <Spinner />
        ) : (
          <>
            <EmotionChart
              data={emotionResponse?.data?.data?.[movie?.media_item_id]}
              isLoading={emotionResponse?.isLoading}
              title={movie?.title}
            />
          </>
        )}
      </Flex>
    </Card>
  ));
};

const CompareInterests = () => {
  const { getFormattedBody, selectedValues } = useCompareContext();
  const [post, res] = useCompareInterestsMutation();

  useFormSnackbar(res, "Error Fetching Interests");

  useEffect(() => {
    // Ensure there are selected values before calling the API
    if (selectedValues.length > 1) {
      post(getFormattedBody());
    }
  }, [selectedValues, post]);
  const colors = useToken("colors", [
    "#264653",
    "#2a9d8f",
    "#e9c46a",
    "#f4a261",
    "#e76f51",
    "black",
  ]);

  const titles = Object.keys(res?.data?.data || {});

  return (
    <GridItem colSpan={selectedValues?.length}>
      <Card>
        <Flex p="3" height="100%" justifyContent="center" alignItems="center">
          {res?.isLoading ? (
            <Spinner />
          ) : (
            <Box height="250px" width="100%">
              <ResponsiveContainer>
                <BarChart data={res?.data?.graph_data}>
                  <XAxis dataKey="name" />
                  <Tooltip />
                  <Legend />
                  {selectedValues?.map((ithTitle, idx) => (
                    <Bar dataKey={ithTitle?.subtitle} fill={colors[idx]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </Flex>
      </Card>
    </GridItem>
  );
};

const CompareAudience = () => {
  const { getFormattedBody, selectedValues } = useCompareContext();
  const [post, res] = useCompareAudienceMutation();

  useFormSnackbar(res, "Error Fetching Audience");

  useEffect(() => {
    // Ensure there are selected values before calling the API
    if (selectedValues.length > 1) {
      post(getFormattedBody());
    }
  }, [selectedValues, post]);

  return (
    <SimpleGrid columns={5} gap={3}>
      {selectedValues?.map((ithAudience) => (
        <>
          <GridItem>{ithAudience?.subtitle || ithAudience?.title}</GridItem>
          <GridItem colSpan={4}>
            <AudienceBase
              audienceData={res?.data?.[ithAudience?.media_item_id]}
              isLoading={res?.isLoading}
            />
          </GridItem>
        </>
      ))}
    </SimpleGrid>
  );
};

const OverviewCompare = () => {
  const { selectedValues } = useCompareContext();

  const comparisonElements = [
    {
      key: "sentiment",
      label: "Composite Score",
      component: <CompareComposite />,
    },

    {
      key: "emotionalResponse",
      label: "Emotional Response",
      component: <EmotionalCompare />,
    },
    {
      key: "interests",
      label: "Interest Drivers",
      component: <CompareInterests />,
    },
    // Add more comparison elements as needed
  ];
  if (selectedValues?.length < 2) {
    return (
      <Flex p="10" color="gray.400">
        <EmptyState
          title={"Awaiting Selection"}
          description={
            "Please select a set of titles to begin comparing  data. Once selected, you'll be able to view and compare  results here."
          }
        />
      </Flex>
    );
  }

  return (
    <Box overflowX="auto">
      <Box my="3">
        <Banner status="info" borderRadius="md" fontSize="sm">
          <BannerTitle>Why do I only see some data?</BannerTitle>
          <BannerDescription>
            As we introduce new features, certain elements of our historical
            data may be lacking, which prevents a comprehensive comparison.
          </BannerDescription>
        </Banner>
      </Box>
      <Grid
        templateColumns={`minmax(75px, 0.5fr) repeat(${selectedValues?.length}, minmax(120px, 1fr))`}
        gap={3}
      >
        {/* Header row for movie titles, starting with an empty cell for the labels column */}
        <Box p={5} />
        {selectedValues?.map((movie) => (
          <Card key={movie} p={5}>
            <Text fontWeight="bold" fontSize={"sm"}>
              {movie?.title}
            </Text>
            <Text fontSize={"xs"} color="gray.600">
              {movie?.subtitle}
            </Text>
          </Card>
        ))}

        {/* Rows for each comparison element */}
        {comparisonElements.map((element) => (
          <React.Fragment key={element.key}>
            {/* Label for the comparison element */}
            <Card p={5}>
              <Text fontWeight="bold">{element.label}</Text>
              {element?.children}
            </Card>

            {element?.component}
          </React.Fragment>
        ))}
      </Grid>
    </Box>
  );
};
export default Compare;
